import React, { useState, useRef, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import styles from './RichTextEditor.module.css';
import { api, endpoints } from '../apiConfig';
import { sanitizeHTML } from '../utils/security';

const RichTextEditor = ({ 
  value, 
  onChange, 
  section, 
  placeholder,
  enableAI = true 
}) => {
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhancedContent, setEnhancedContent] = useState(null);
  const [error, setError] = useState(null);
  const quillRef = useRef(null);

  // Configure Quill editor to disable browser autocorrect/autocomplete
  useEffect(() => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const editorElement = editor.root;
      
      // Disable browser autocorrect and autocomplete features
      editorElement.setAttribute('autocorrect', 'off');
      editorElement.setAttribute('autocapitalize', 'off');
      editorElement.setAttribute('autocomplete', 'off');
      editorElement.setAttribute('spellcheck', 'false');
      editorElement.setAttribute('data-gramm', 'false'); // Disable Grammarly
      editorElement.setAttribute('data-ms-editor', 'false'); // Disable MS Editor
    }
  }, []);

  // Configure toolbar with essential formatting options
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }], // Headers and normal text
      [{ 'size': ['small', false, 'large', 'huge'] }], // Font sizes
      [{ 'font': [] }], // Font family
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }], // Text and background colors
      ['link'],
      ['clean'] // Remove formatting
    ],
  };

  const formats = [
    'header', 'size', 'font', 'bold', 'italic', 'underline',
    'list', 'bullet', 'color', 'background', 'link'
  ];

  const handleEnhance = async () => {
    try {
      setIsEnhancing(true);
      setError(null);

      // Convert HTML to plain text for AI processing
      const plainText = quillRef.current?.getEditor()?.getText() || '';

      const response = await api.post(endpoints.user.enhance, {
        content: plainText,
        section
      });

      if (response.data.enhanced) {
        // Convert enhanced plain text back to simple HTML
        const enhancedHtml = response.data.enhanced
          .split('\n')
          .filter(line => line.trim())
          .map(line => `<p>${line}</p>`)
          .join('');
        
        setEnhancedContent(enhancedHtml);
      } else {
        throw new Error('No enhanced content received');
      }
    } catch (error) {
      console.error('Enhancement error:', error);
      setError(error.response?.data?.message || error.message || 'Failed to enhance content. Please try again.');
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleUseEnhanced = () => {
    onChange(enhancedContent);
    setEnhancedContent(null);
  };

  const handleKeepOriginal = () => {
    setEnhancedContent(null);
  };

  return (
    <div className={styles.container}>
      <div className={styles.editorWrapper}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ''}
          onChange={onChange}
          placeholder={placeholder}
          modules={modules}
          formats={formats}
          className={styles.editor}
        />
        
        {enableAI && (
          <div className={styles.enhanceSection}>
            <button
              type="button"
              onClick={handleEnhance}
              disabled={isEnhancing || !value}
              className={styles.enhanceButton}
            >
              {isEnhancing ? 'Enhancing...' : '✨ AI Enhance'}
            </button>
            
            {error && (
              <div className={styles.error}>{error}</div>
            )}
          </div>
        )}
      </div>

      {enhancedContent && (
        <div className={styles.enhancedPreview}>
          <h4>Enhanced Content Preview:</h4>
          <div 
            className={styles.previewContent}
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(enhancedContent) }}
          />
          <div className={styles.previewActions}>
            <button onClick={handleUseEnhanced} className={styles.useButton}>
              Use Enhanced
            </button>
            <button onClick={handleKeepOriginal} className={styles.keepButton}>
              Keep Original
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RichTextEditor; 